"use client";

import React, { useEffect, useRef, useState } from "react";
import { Crown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function TheAnchorOurGoldStandard() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: mockupRef,
    isVisible: isMockupVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="the-anchor-our-gold-standard" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute -right-40 -top-40 z-0 opacity-5 select-none md:-right-20 md:-top-20">
        <Crown className="h-[600px] w-[600px] -rotate-12 text-primary lg:h-[800px] lg:w-[800px]" strokeWidth={0.5} />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-12">
          
          {}
          <div ref={textRef} className={`flex flex-col items-start space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
            <div className="space-y-4">
              <h2 className="font-serif text-4xl leading-tight text-primary md:text-5xl lg:text-6xl">
                The Anchor: <br />
                <span className="text-foreground">Our Gold Standard</span>
              </h2>
              <div className="h-1 w-24 bg-primary/50 rounded-full" />
            </div>
            
            <p className="font-sans text-lg leading-relaxed tracking-wide text-muted-foreground md:text-xl">Experience the pinnacle of digital pedigree. We don't just build websites; we craft digital assets. Explore our own heritage kennel site, the benchmark for every Custom Website project we undertake. It embodies the perfect balance of luxurious aesthetics, health-testing transparency, and seamless inquiry management.</p>

            <div className="pt-4">
              <Button asChild variant="outline" size="lg" className="group rounded-full border-primary bg-transparent px-8 py-6 text-lg tracking-wider text-primary transition-all hover:bg-primary hover:text-primary-foreground">
                <Link to="/OurServicesPage">
                  Explore The Anchor
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={mockupRef} className={`relative mx-auto w-full max-w-2xl transition-all duration-1000 delay-300 ease-out ${isMockupVisible ? "translate-x-0 opacity-100" : "translate-x-12 opacity-0"}`}>
            {}
            <div className="relative z-10 rounded-t-2xl border-[10px] border-muted bg-muted shadow-2xl ring-1 ring-border/30 md:border-[14px]">
              {}
              <div className="absolute -top-2 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-background/50 md:-top-2.5 md:h-2 md:w-2" />
              
              {}
              <div className="relative aspect-[16/10] overflow-hidden rounded-sm bg-background">
                <ScrollArea className="h-full w-full">
                  {}
                  <div className="flex flex-col">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1453987/img/anchor_kennel_hero.png" alt="The Anchor Kennel Website Hero" className="h-auto w-full object-cover" />
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1453987/img/anchor_kennel_dogs.png" alt="The Anchor Kennel Dogs Showcase" className="h-auto w-full object-cover" />
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1453987/img/anchor_kennel_footer.png" alt="The Anchor Kennel Footer" className="h-auto w-full object-cover" />
                  </div>
                </ScrollArea>
              </div>
            </div>
            
            {}
            <div className="relative z-0 h-4 w-[110%] -translate-x-[4.5%] rounded-b-xl rounded-t-sm bg-muted-foreground shadow-[0_20px_50px_rgba(0,0,0,0.5)] md:h-5 md:w-[114%] md:-translate-x-[6%]">
              {}
              <div className="absolute top-0 left-1/2 h-1.5 w-1/5 -translate-x-1/2 rounded-b-md bg-muted/30 md:h-2" />
            </div>
          </div>

        </div>
      </div>
    </section>;
}